import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Divider, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { FiCheckCircle, FiClock, FiHome, FiMapPin, FiPackage, FiTruck } from "react-icons/fi";
import { useCookies } from "react-cookie";
import axios from "axios";
import WithSubnavigation from "./navbar";
import { API_BASE } from "../../config";

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${name}=`));
};

const colors = {
  brand: { 50: "#f0fdfa", 500: "#0d9488", 600: "#0f766e" },
  neutral: { 50: "#f8fafc", 100: "#f1f5f9", 200: "#e2e8f0", 400: "#94a3b8", 500: "#64748b", 700: "#334155", 800: "#1e293b", 900: "#0f172a" },
};

const formatCurrency = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function OrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [cookies, , removeCookie] = useCookies(["token", "role"]);
  const isLoggedIn = Boolean(cookies.token || hasCookie("token"));
  const [username, setUsername] = useState("");
  const [fullUser, setFullUser] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchUser = async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (data?.status && data?.user) {
          setUsername(data.user);
          const fullUserData = await axios.get(`${API_BASE}/patient/myInfo/${data.user}`);
          setFullUser(fullUserData.data);
        }
      } catch {}
    };
    fetchUser();
  }, [isLoggedIn]);

  const state = location.state || {};
  const [fetchedOrder, setFetchedOrder] = useState(null);
  const orderId = state.order?._id || state.order?.id || null;

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API_BASE}/order/${orderId}`, { withCredentials: true })
      .then(({ data }) => setFetchedOrder(data))
      .catch((err) => console.error("Failed to fetch order:", err));
  }, [orderId]);

  const order = fetchedOrder || state.order || {};
  const pharmacy = state.pharmacy || order.pharmacy || {};
  const cartItems = Array.isArray(state.medicines) ? state.medicines : [];
  const deliveryOption = order.deliveryWorkflow?.selectedOption || state.deliveryOption || null;
  const deliveryAddress = order.deliveryLocation?.address || order.address || state.deliveryAddress || "";

  // pharmacistReviewing = came from checkout, payment not yet done
  const pharmacistReviewing = Boolean(state.pharmacistReviewing);
  const paymentDone = Boolean(order?.paymentWorkflow?.isFinalized) || !pharmacistReviewing;

  const medicinesSubtotal = cartItems.reduce(
    (sum, ci) => sum + Number(ci.price || 0) * Number(ci.quantity || 1),
    0
  );
  const deliveryFee = Number(deliveryOption?.deliveryFee || 0);
  const calculatedTotal = medicinesSubtotal + deliveryFee;
  const orderTotal = order?.paymentWorkflow?.isFinalized
    ? Number(order?.paymentWorkflow?.payableAmount || 0)
    : calculatedTotal;

  const logout = () => {
    removeCookie("token", { path: "/" });
    removeCookie("role", { path: "/" });
    navigate("/home", { replace: true });
  };

  return (
    <Box minH="100vh" bg={colors.neutral[50]} overflowX="hidden">
      <WithSubnavigation
        username={isLoggedIn ? username : ""}
        name={isLoggedIn ? fullUser?.Name : ""}
        isLoggedIn={isLoggedIn}
        sidebarOffset="0px"
        onLogout={logout}
      />

      <Flex direction="column" align="center" justify="center" minH="calc(100vh - 74px)" px={4} py={10}>
        <Box
          bg="white"
          borderRadius="20px"
          border="1px solid"
          borderColor={colors.neutral[200]}
          boxShadow="0 4px 24px rgba(15,23,42,0.07)"
          maxW="480px"
          w="full"
          overflow="hidden"
        >
          {/* Header — two states */}
          <Box
            bg={pharmacistReviewing
              ? "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)"
              : "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"}
            px={6}
            py={8}
            textAlign="center"
          >
            <Flex justify="center" mb={3}>
              <Box
                w="64px" h="64px" borderRadius="full"
                bg="rgba(255,255,255,0.2)"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Icon as={pharmacistReviewing ? FiClock : FiCheckCircle} color="white" boxSize={8} />
              </Box>
            </Flex>
            <Text fontWeight="900" fontSize="22px" color="white" m={0} letterSpacing="-0.02em">
              {pharmacistReviewing ? "Order sent to pharmacy!" : "Order Confirmed!"}
            </Text>
            <Text fontSize="13px" color="rgba(255,255,255,0.8)" mt={1} m={0}>
              {pharmacistReviewing
                ? "The pharmacist is reviewing your order. You'll be notified when it's ready for payment."
                : "Your payment is confirmed. The pharmacy will prepare and dispatch your order shortly."}
            </Text>
            {orderId && (
              <Text fontSize="11px" color="rgba(255,255,255,0.6)" mt={2} m={0}>
                Order ID: #{String(orderId).slice(-8).toUpperCase()}
              </Text>
            )}
          </Box>

          {/* Details */}
          <Stack spacing={0} divider={<Divider borderColor={colors.neutral[100]} />}>

            {/* Pharmacy */}
            {(pharmacy.name || pharmacy.pharmacyName) && (
              <Flex align="center" gap={3} px={5} py={4}>
                <Box w="32px" h="32px" borderRadius="8px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Icon as={FiMapPin} color={colors.brand[600]} boxSize={3.5} />
                </Box>
                <Box minW={0}>
                  <Text fontSize="11px" fontWeight="700" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.06em" m={0}>Pharmacy</Text>
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[900]} noOfLines={1} m={0}>{pharmacy.name || pharmacy.pharmacyName}</Text>
                  {pharmacy.address && (
                    <Text fontSize="11px" color={colors.neutral[500]} noOfLines={1} m={0}>{pharmacy.address}</Text>
                  )}
                </Box>
              </Flex>
            )}

            {/* Delivery address */}
            {deliveryAddress && (
              <Flex align="center" gap={3} px={5} py={4}>
                <Box w="32px" h="32px" borderRadius="8px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Icon as={FiTruck} color={colors.brand[600]} boxSize={3.5} />
                </Box>
                <Box minW={0}>
                  <Text fontSize="11px" fontWeight="700" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.06em" m={0}>Deliver to</Text>
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[900]} noOfLines={2} m={0}>{deliveryAddress}</Text>
                  {deliveryOption?.etaLabel && (
                    <Text fontSize="11px" color={colors.brand[600]} fontWeight="600" m={0}>{deliveryOption.etaLabel}</Text>
                  )}
                </Box>
              </Flex>
            )}

            {/* Medicines - only show when payment is complete, not during pharmacist review */}
            {!pharmacistReviewing && cartItems.length > 0 && (
              <Box px={5} py={4}>
                <Flex align="center" gap={3} mb={3}>
                  <Box w="32px" h="32px" borderRadius="8px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                    <Icon as={FiPackage} color={colors.brand[600]} boxSize={3.5} />
                  </Box>
                  <Text fontSize="11px" fontWeight="700" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.06em" m={0}>
                    {cartItems.length} Medicine{cartItems.length !== 1 ? "s" : ""}
                  </Text>
                </Flex>
                <Stack spacing={1} pl="44px">
                  {cartItems.map((ci, i) => (
                    <Flex key={i} justify="space-between" align="center">
                      <Text fontSize="12px" color={colors.neutral[700]} noOfLines={1} m={0} flex={1} pr={2}>
                        {ci.name || ci.product_name || "Medicine"} × {ci.quantity || 1}
                      </Text>
                      {ci.price > 0 && (
                        <Text fontSize="12px" fontWeight="700" color={colors.neutral[700]} m={0} flexShrink={0}>
                          {formatCurrency(Number(ci.price) * Number(ci.quantity || 1))}
                        </Text>
                      )}
                    </Flex>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Total row - only show when payment is complete, not during pharmacist review */}
            {!pharmacistReviewing && (
              <Flex px={5} py={4} justify="space-between" align="center" bg={colors.neutral[50]}>
                <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0}>
                  Total paid
                </Text>
                <Text fontSize="18px" fontWeight="900" color={colors.brand[600]} m={0}>
                  {orderTotal > 0 ? formatCurrency(orderTotal) : "—"}
                </Text>
              </Flex>
            )}

          </Stack>

          {/* Actions */}
          <Box px={5} py={5} display="flex" flexDirection="column" gap={2}>
            {pharmacistReviewing && orderId && (
              <Button
                w="full"
                size="md"
                variant="outline"
                borderColor={colors.brand[600]}
                color={colors.brand[600]}
                borderRadius="10px"
                fontWeight="700"
                onClick={() => navigate(`/order/prescription/${orderId}`)}
              >
                View order details
              </Button>
            )}
            <Button
              w="full"
              size="md"
              bg="linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"
              color="white"
              _hover={{ bg: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)" }}
              borderRadius="10px"
              fontWeight="700"
              leftIcon={<FiHome />}
              onClick={() => navigate("/home")}
            >
              Back to Home
            </Button>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
